import { useState, useCallback, useEffect } from "react";
import type { ChartConfig, StatCardConfig } from "@hooks/use-reports/types";
import type {
  AddedChartEntry,
  AddedStatCardEntry,
  ChartOverride,
  OverrideConfig,
  StatCardOverride,
  UseReportLayoutProps,
} from "./types";
import { generateStatCardId, generateChartId } from "./utils";

export type {
  AddedChartEntry,
  AddedStatCardEntry,
  ChartOverride,
  LayoutItem,
  OverrideConfig,
  SaveUserConfigPayload,
  StatCardOverride,
  UseReportLayoutProps,
} from "./types";

/**
 * Manages user report layout customizations (show/hide, reorder, add charts).
 * Tracks whether this is a new customization (create) or existing (update).
 * Note: User ID is extracted from the authenticated request on the backend.
 */
export const useReportLayout = ({
  reportId,
  initialCharts = [],
  initialStatCards = [],
  userConfig,
}: UseReportLayoutProps) => {
  const [layout, setLayout] = useState(() => ({
    charts: (initialCharts as ChartConfig[]).map((c, i) => {
      const override = userConfig?.charts?.[c.id as string];
      return {
        id: c.id as string,
        order: override?.order ?? i,
        hidden: override?.hidden ?? false,
      };
    }),
    statCards: (initialStatCards || []).map((c, i) => {
      const override = userConfig?.statCards?.[c.id as string];
      return {
        id: c.id as string,
        order: override?.order ?? i,
        hidden: override?.hidden ?? false,
      };
    }),
  }));

  const [additionalCharts, setAdditionalCharts] = useState<AddedChartEntry[]>(() => {
    if (!userConfig?.addedCharts) return [];
    return Object.entries(userConfig.addedCharts).map(([id, config]) => ({ id, config }));
  });
  const [additionalStatCards, setAdditionalStatCards] = useState<AddedStatCardEntry[]>(() => {
    if (!userConfig?.addedStatCards) return [];
    return Object.entries(userConfig.addedStatCards).map(([id, config]) => ({ id, config }));
  });

  // Reset layout when report or user config changes
  useEffect(() => {
    let statCardsLayout = (initialStatCards || []).map((c, i) => {
      const override = userConfig?.statCards?.[c.id as string];
      return {
        id: c.id as string,
        order: override?.order ?? i,
        hidden: override?.hidden ?? false,
      };
    });

    if (userConfig?.addedStatCards) {
      const addedCardIds = Object.keys(userConfig.addedStatCards);
      addedCardIds.forEach((cardId) => {
        const override = userConfig?.statCards?.[cardId];
        const order = override?.order ?? statCardsLayout.length;
        const hidden = override?.hidden ?? false;
        statCardsLayout.push({ id: cardId, order, hidden });
      });
      statCardsLayout = statCardsLayout.sort((a, b) => a.order - b.order);
    }

    let chartsLayout = (initialCharts as ChartConfig[]).map((c, i) => ({
      id: c.id as string,
      order: userConfig?.charts?.[c.id as string]?.order ?? i,
      hidden: userConfig?.charts?.[c.id as string]?.hidden ?? false,
    }));

    if (userConfig?.addedCharts) {
      const addedChartIds = Object.keys(userConfig.addedCharts);
      addedChartIds.forEach((chartId) => {
        const override = userConfig?.charts?.[chartId];
        const order = override?.order ?? chartsLayout.length;
        const hidden = override?.hidden ?? false;
        chartsLayout.push({ id: chartId, order, hidden });
      });
      chartsLayout = chartsLayout.sort((a, b) => a.order - b.order);
    }

    setLayout({ charts: chartsLayout, statCards: statCardsLayout });

    setAdditionalCharts(
      userConfig?.addedCharts
        ? Object.entries(userConfig.addedCharts).map(([id, config]) => ({ id, config }))
        : []
    );
    setAdditionalStatCards(
      userConfig?.addedStatCards
        ? Object.entries(userConfig.addedStatCards).map(([id, config]) => ({ id, config }))
        : []
    );
    // Intentionally excludes initialCharts/initialStatCards: this effect resets layout only on
    // reportId/userConfig change (see comment above), not on new-but-equal array references from the caller
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, userConfig]);

  const addChart = useCallback((config: ChartConfig): string => {
    const chartId = generateChartId();
    const configWithId: ChartConfig = { ...config, id: chartId };
    setAdditionalCharts((prev) => [{ id: chartId, config: configWithId }, ...prev]);
    setLayout((prev) => ({
      ...prev,
      charts: [
        { id: chartId, order: 0, hidden: false },
        ...prev.charts.map((chart) => ({ ...chart, order: chart.order + 1 })),
      ],
    }));
    return chartId;
  }, []);

  const addStatCard = useCallback((config: StatCardConfig) => {
    const cardId = generateStatCardId();
    const configWithId: StatCardConfig = { ...config, id: cardId };
    setAdditionalStatCards((prev) => [{ id: cardId, config: configWithId }, ...prev]);
    setLayout((prev) => ({
      ...prev,
      statCards: [
        { id: cardId, order: 0, hidden: false },
        ...prev.statCards.map((card) => ({ ...card, order: card.order + 1 })),
      ],
    }));
    return cardId;
  }, []);

  const updateStatCard = useCallback((cardId: string, config: StatCardConfig) => {
    const configWithId: StatCardConfig = { ...config, id: cardId };
    setAdditionalStatCards((prev) =>
      prev.map((card) => (card.id === cardId ? { ...card, config: configWithId } : card))
    );
  }, []);

  const removeStatCard = useCallback((cardId: string) => {
    setAdditionalStatCards((prev) => prev.filter((card) => card.id !== cardId));
    setLayout((prev) => ({
      ...prev,
      statCards: prev.statCards.filter((c) => c.id !== cardId),
    }));
  }, []);

  const updateChart = useCallback((chartId: string, config: ChartConfig) => {
    const configWithId: ChartConfig = { ...config, id: chartId };
    setAdditionalCharts((prev) =>
      prev.map((chart) => (chart.id === chartId ? { ...chart, config: configWithId } : chart))
    );
  }, []);

  const removeChart = useCallback((chartId: string) => {
    setAdditionalCharts((prev) => prev.filter((chart) => chart.id !== chartId));
    setLayout((prev) => ({
      ...prev,
      charts: prev.charts.filter((c) => c.id !== chartId),
    }));
  }, []);

  const buildOverrideConfig = useCallback((): OverrideConfig => {
    const overrideConfig: OverrideConfig = {};

    const chartsOverrides: Record<string, ChartOverride> = {};
    layout.charts.forEach((chart, idx) => {
      chartsOverrides[chart.id] = { hidden: chart.hidden, order: idx };
    });
    if (Object.keys(chartsOverrides).length > 0) {
      overrideConfig.charts = chartsOverrides;
    }

    const statCardsOverrides: Record<string, StatCardOverride> = {};
    layout.statCards.forEach((card, idx) => {
      statCardsOverrides[card.id] = { hidden: card.hidden, order: idx };
    });
    if (Object.keys(statCardsOverrides).length > 0) {
      overrideConfig.statCards = statCardsOverrides;
    }

    if (additionalStatCards.length > 0) {
      const addedStatCardsRecord: Record<string, StatCardConfig> = {};
      additionalStatCards.forEach((card) => {
        addedStatCardsRecord[card.id] = card.config;
      });
      overrideConfig.addedStatCards = addedStatCardsRecord;
    }

    if (additionalCharts.length > 0) {
      const addedChartsRecord: Record<string, ChartConfig> = {};
      additionalCharts.forEach((chart) => {
        addedChartsRecord[chart.id] = chart.config;
      });
      overrideConfig.addedCharts = addedChartsRecord;
    }

    return overrideConfig;
  }, [layout.charts, layout.statCards, additionalStatCards, additionalCharts]);

  return {
    layout,
    additionalCharts,
    additionalStatCards,
    addChart,
    updateChart,
    removeChart,
    addStatCard,
    updateStatCard,
    removeStatCard,
    buildOverrideConfig,
  };
};
